// Sugalarm — phone-side pairing approval. JWT-only.
//
// The 6-digit entry form renders ONLY for a JWT session. Cycle 175: a
// device-token session (the kiosk) that navigates here directly is
// redirected to /now and never sees an input field — the approve endpoint
// is JWT-only and 403s device tokens by design, so the kiosk has no spot
// to type a code at the route level, not just in the button. The kiosk
// OFFERS a code (via /pair force-display) instead of asking for one.

import { useState } from "react";
import { Navigate } from "react-router-dom";

import * as pairingApi from "../../api/pairing";
import { Button } from "../../components/ui/Button";
import { Field, inputClass } from "../../components/ui/Field";
import { Pill } from "../../components/ui/Pill";
import { isAuthedAsDevice } from "../../lib/auth";

type Outcome =
  | { kind: "idle" }
  | { kind: "ok" }
  | { kind: "err"; message: string };

export function PairingApprovePanel() {
  const [code, setCode] = useState("");
  const [name, setName] = useState("Kitchen kiosk");
  const [type, setType] = useState<"kiosk" | "web" | "mobile" | "other">(
    "kiosk",
  );
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>({ kind: "idle" });

  // Cycle 175: device-token sessions never see the entry form — redirect
  // to /now. (The cycle-25 predicate: a device token means this is the
  // kiosk, which offers a code, it doesn't type one.)
  if (isAuthedAsDevice()) {
    return <Navigate to="/now" replace />;
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    setOutcome({ kind: "idle" });
    const r = await pairingApi.approvePairing({
      code,
      device_name: name || undefined,
      device_type: type,
    });
    setBusy(false);
    if (!r.ok) {
      setOutcome({
        kind: "err",
        message:
          r.error.kind === "not_found"
            ? "That code is invalid or expired. Ask the kiosk for a new one."
            : r.error.message,
      });
      return;
    }
    setOutcome({ kind: "ok" });
    setCode("");
  };

  // Allow paste of a 6-digit string anywhere; strip non-digits and clamp.
  const onCodeChange = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
  };

  return (
    <div
      // Cycle 453.1: shrink and scroll inside the frame. With content taller
      // than the space (the 1024x600 kiosk) the panel kept its full height and
      // pushed the frame's No Data banner below the stage, where Silence could
      // not be tapped.
      style={{
        padding: "24px 32px",
        maxWidth: 520,
        display: "flex",
        flexDirection: "column",
        gap: 14,
        flex: "1 1 auto",
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      <div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em" }}>
          Pair a device
        </h1>
        <p style={{ color: "var(--ink-2)", marginTop: 4, fontSize: 14 }}>
          Enter the 6-digit code shown on the kiosk. Pairing creates a device
          token scoped to your account that the kiosk uses for all future
          requests — its plaintext is never stored anywhere except on the
          kiosk itself.
        </p>
      </div>

      <form
        onSubmit={submit}
        style={{
          background: "var(--bg-elev)",
          border: "0.5px solid var(--line-strong)",
          borderRadius: 14,
          padding: 18,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <Field label="Pairing code">
          <input
            inputMode="numeric"
            pattern="[0-9]*"
            autoFocus
            maxLength={6}
            value={code}
            onChange={(e) => onCodeChange(e.target.value)}
            placeholder="000000"
            className={`${inputClass} tnum`}
            style={{
              fontSize: 32,
              letterSpacing: "0.4em",
              textAlign: "center",
              padding: "16px 12px",
              fontFamily: "var(--hero-font)",
              fontWeight: 600,
            }}
          />
        </Field>

        <Field label="Device name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
            placeholder="Kitchen kiosk"
          />
        </Field>

        <Field label="Device type">
          <select
            value={type}
            onChange={(e) =>
              setType(e.target.value as "kiosk" | "web" | "mobile" | "other")
            }
            className={inputClass}
          >
            <option value="kiosk">Kiosk</option>
            <option value="web">Web</option>
            <option value="mobile">Mobile</option>
            <option value="other">Other</option>
          </select>
        </Field>

        <Button type="submit" full disabled={busy || code.length !== 6}>
          {busy ? "Approving…" : "Approve pairing"}
        </Button>

        {outcome.kind === "ok" && (
          <Pill tone="ok">
            Paired. The kiosk should switch to the Now screen within a few seconds.
          </Pill>
        )}
        {outcome.kind === "err" && (
          <Pill tone="urgent">{outcome.message}</Pill>
        )}
      </form>
    </div>
  );
}
